import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { quoteService } from '../services/quoteService';
import { hvacQuickStartTemplates } from '../data/hvacQuickStartTemplates';
import { analytics, trackQuoteCreated as trackInternalQuoteCreated } from '../services/analytics';
import {
  trackQuoteCreated as trackGA4QuoteCreated,
  trackQuoteLinkCopied
} from '../services/ga4Analytics';
import {
  ArrowLeft,
  Bookmark,
  Save,
  X,
  Star,
  Trash2,
  Plus,
  Settings,
  CheckCircle,
  Copy,
  ClipboardCheck,
  Palette,
  FileText,
  Share2,
  Lightbulb
} from 'lucide-react';

/* ---------- Re-usable helpers ---------- */
const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="mobile-modal bg-white border border-black rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 md:p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-instrument text-xl md:text-2xl tracking-tight">{title}</h3>
          <button 
            onClick={onClose}
            className="mobile-action-btn hover:bg-gray-100"
            aria-label="Close modal"
          >
            <X size={20} className="md:w-6 md:h-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const FormInput = ({ label, ...rest }) => (
  <div>
    <label className="block text-sm md:text-base font-semibold mb-2 font-jetbrains">{label}</label>
    <input
      {...rest}
      className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base min-h-[48px]"
    />
  </div>
);

const FormTextarea = ({ label, ...rest }) => (
  <div>
    <label className="block text-sm md:text-base font-semibold mb-2 font-jetbrains">{label}</label>
    <textarea
      {...rest}
      className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base min-h-[120px]"
    />
  </div>
);

const PrimaryButton = ({ children, disabled, className = "", ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    className={`px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 disabled:opacity-50 min-h-[48px] text-base md:text-lg ${className}`}
  >
    {children}
  </button>
);

const getQuoteTotalValue = (quoteData) => {
  const prices = Object.values(quoteData.tiers || {})
    .map((tier) => Number.parseFloat(tier?.price || 0))
    .filter((price) => Number.isFinite(price));
  return prices.length ? Math.max(...prices) : 0;
};

/* ---------- Header ---------- */
const QuoteBuilderHeader = ({ onBack }) => (
  <header className="border-b border-black bg-white sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5 flex items-center space-x-3 md:space-x-4">
      <button 
        onClick={onBack} 
        className="mobile-action-btn -ml-2 hover:bg-gray-100"
        aria-label="Go back"
      >
        <ArrowLeft size={20} className="md:w-6 md:h-6" />
      </button>
      <div>
        <h1 className="font-instrument text-2xl md:text-3xl lg:text-4xl tracking-tight">Create New Quote</h1>
        <p className="font-jetbrains text-gray-600 mt-1 text-sm md:text-base">
          Build your Good, Better, Best interactive quote
        </p>
      </div>
    </div>
  </header>
);

const normalizeRebate = rebate => ({
  label: rebate?.label || '',
  amountType: rebate?.amountType || rebate?.amount_type || 'fixed',
  amountValue: rebate?.amountValue ?? rebate?.amount_value ?? '',
  notes: rebate?.notes || ''
});

const normalizeAddon = (addon, index) => ({
  label: addon?.label || '',
  unitPrice: addon?.unitPrice ?? addon?.unit_price ?? '',
  quantityMin: addon?.quantityMin ?? addon?.quantity_min ?? 0,
  quantityMax: addon?.quantityMax ?? addon?.quantity_max ?? 10,
  defaultQuantity: addon?.defaultQuantity ?? addon?.default_quantity ?? 1,
  tierLevel: addon?.tierLevel || addon?.tier_level || 'all',
  isPreselected: Boolean(addon?.isPreselected ?? addon?.is_preselected),
  displayOrder: addon?.displayOrder ?? addon?.display_order ?? index
});

const normalizeTier = (template, tierKey, previousTier) => {
  const equipmentMetadata = template?.equipment_metadata?.[tierKey] || {};
  const templateBullets = template?.[`${tierKey}_bullet_points`];
  const bullets = Array.isArray(templateBullets) && templateBullets.length > 0
    ? templateBullets
    : previousTier.bulletPoints;

  return {
    ...previousTier,
    title: template?.[`${tierKey}_title`] ?? previousTier.title,
    price: template?.[`${tierKey}_price`] ?? previousTier.price,
    description: template?.[`${tierKey}_description`] ?? previousTier.description,
    bulletPoints: bullets,
    equipment: {
      ...previousTier.equipment,
      ...equipmentMetadata
    }
  };
};

const applyTemplateToForm = (template, previousForm) => ({
  ...previousForm,
  templateUsedId: template.id,
  interactiveEnabled:
    typeof template.interactive_enabled === 'boolean'
      ? template.interactive_enabled
      : previousForm.interactiveEnabled,
  showMonthlyPayments:
    typeof template.show_monthly_payments === 'boolean'
      ? template.show_monthly_payments
      : previousForm.showMonthlyPayments,
  financingApr:
    template.financing_apr ?? previousForm.financingApr,
  financingTermMonths:
    template.financing_term_months ?? previousForm.financingTermMonths,
  financingDownPayment:
    template.financing_down_payment ?? previousForm.financingDownPayment,
  rebates: Array.isArray(template.rebates)
    ? template.rebates.map(normalizeRebate)
    : previousForm.rebates,
  addons: Array.isArray(template.addons)
    ? template.addons.map((addon, index) => normalizeAddon(addon, index))
    : previousForm.addons,
  tiers: {
    good: normalizeTier(template, 'good', previousForm.tiers.good),
    better: normalizeTier(template, 'better', previousForm.tiers.better),
    best: normalizeTier(template, 'best', previousForm.tiers.best)
  }
});

/* ---------- Main ---------- */
const QuoteBuilder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // ADDED: Template modal state management
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [saveTemplateForm, setSaveTemplateForm] = useState({
    templateName: '',
    templateDescription: ''
  });

  const initialFormState = {
    clientName: '',
    clientEmail: '',
    interactiveEnabled: false,
    templateUsedId: null,
    // HVAC P0: Monthly payment fields
    showMonthlyPayments: false,
    financingApr: 8.99,
    financingTermMonths: 60,
    financingDownPayment: 0,
    // HVAC P0: Rebates & incentives
    rebates: [],
    // HVAC P0: Scope/allowance adders
    addons: [],
    tiers: {
      good: { 
        title: '', 
        price: '', 
        description: '', 
        bulletPoints: [''],
        // HVAC P0: Equipment & efficiency metadata
        equipment: {
          modelOutdoor: '',
          modelIndoor: '',
          modelFurnace: '',
          ahriReference: '',
          ahriCertificateUrl: '',
          seer2: '',
          hspf2: '',
          afue: ''
        }
      },
      better: { 
        title: '', 
        price: '', 
        description: '', 
        bulletPoints: [''],
        // HVAC P0: Equipment & efficiency metadata
        equipment: {
          modelOutdoor: '',
          modelIndoor: '',
          modelFurnace: '',
          ahriReference: '',
          ahriCertificateUrl: '',
          seer2: '',
          hspf2: '',
          afue: ''
        }
      },
      best: { 
        title: '', 
        price: '', 
        description: '', 
        bulletPoints: [''],
        // HVAC P0: Equipment & efficiency metadata
        equipment: {
          modelOutdoor: '',
          modelIndoor: '',
          modelFurnace: '',
          ahriReference: '',
          ahriCertificateUrl: '',
          seer2: '',
          hspf2: '',
          afue: ''
        }
      }
    }
  };
  const [formData, setFormData] = useState(initialFormState);

  /* ---------- effects ---------- */
  const loadTemplates = useCallback(async () => {
    try {
      const res = await quoteService.loadPricingPresets();
      const userSavedTemplates = res.data ?? [];
      const list = [...hvacQuickStartTemplates, ...userSavedTemplates];
      setTemplates(list);
      return list;
    } catch (err) {
      console.error(err);   // log for yourself
      setTemplates(hvacQuickStartTemplates);
      return hvacQuickStartTemplates;
    }
  }, []);

  useEffect(() => {
    loadTemplates().then(fetched => {
      const savedDefault = (fetched ?? []).find(t => t.is_default && !t.is_builtin);
      const builtInDefault = (fetched ?? []).find(t => t.is_builtin && t.is_default);
      const def = savedDefault || builtInDefault;
      if (def && !selectedTemplate) loadTemplate(def.id);
    });
  }, [loadTemplates, selectedTemplate]);

  const loadTemplate = useCallback(async id => {
    try {
      const builtInTemplate = hvacQuickStartTemplates.find(template => template.id === id);
      const t = builtInTemplate ? builtInTemplate : (await quoteService.getPresetDetails(id)).data;

      setFormData(prev => applyTemplateToForm(t, prev));
      setSelectedTemplate(t);
      toast.success(`Template "${t.name}" loaded`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load template');
    }
  }, []);

  /* ---------- handlers ---------- */
  // ADDED: Template modal handlers
  const openSaveModal = () => {
    setSaveTemplateForm({ templateName: '', templateDescription: '' });
    setShowSaveModal(true);
  };

  const closeSaveModal = () => {
    setShowSaveModal(false);
    setSaveTemplateForm({ templateName: '', templateDescription: '' });
  };

  const openLoadModal = () => {
    setShowLoadModal(true);
  };

  const closeLoadModal = () => {
    setShowLoadModal(false);
  };

  const clearSelectedTemplate = () => {
    setSelectedTemplate(null);
    setFormData(initialFormState);
  };

  const saveAsTemplate = async () => {
    if (!saveTemplateForm.templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      await quoteService.savePricingPresets({
        name: saveTemplateForm.templateName,
        description: saveTemplateForm.templateDescription,
        isDefault: false,
        tiers: formData.tiers
      });
      toast.success('Template saved!');
      loadTemplates();
      closeSaveModal();
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Failed to save template.');
    }
  };

  const deleteTemplate = async id => {
    const templateToDelete = templates.find(template => template.id === id);
    if (templateToDelete?.is_builtin) {
      toast.info('Built-in starter templates cannot be deleted.');
      return;
    }

    if (!window.confirm('Delete this template?')) return;
    try {
      await quoteService.deletePricingPresets(id);
      toast.success('Template deleted.');
      if (selectedTemplate?.id === id) clearSelectedTemplate();
      loadTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
      toast.error('Failed to delete.');
    }
  };

  /* ---------- form helpers ---------- */
  const handleChange = e => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      // Handle nested fields like "good.title" or "good.equipment.seer2"
      const parts = name.split('.');
      if (parts.length === 2) {
        // Basic tier fields like "good.title"
        const [tier, field] = parts;
        setFormData(prev => ({
          ...prev,
          tiers: {
            ...prev.tiers,
            [tier]: { ...prev.tiers[tier], [field]: value }
          }
        }));
      } else if (parts.length === 3 && parts[1] === 'equipment') {
        // Equipment fields like "good.equipment.seer2"
        const [tier, , equipmentField] = parts;
        setFormData(prev => ({
          ...prev,
          tiers: {
            ...prev.tiers,
            [tier]: { 
              ...prev.tiers[tier], 
              equipment: {
                ...prev.tiers[tier].equipment,
                [equipmentField]: value
              }
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addBullet = tier =>
    setFormData(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tier]: {
          ...prev.tiers[tier],
          bulletPoints: [...prev.tiers[tier].bulletPoints, '']
        }
      }
    }));

  const removeBullet = (tier, idx) =>
    setFormData(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tier]: {
          ...prev.tiers[tier],
          bulletPoints: prev.tiers[tier].bulletPoints.filter((_, i) => i !== idx)
        }
      }
    }));

  // FIXED: Added missing handleBullet function for updating bullet points
  const handleBullet = (tier, index, value) => {
    setFormData(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tier]: {
          ...prev.tiers[tier],
          bulletPoints: prev.tiers[tier].bulletPoints.map((bullet, i) => 
            i === index ? value : bullet
          )
        }
      }
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    console.log('🔥 QUOTE BUILDER - Form submission started');
    console.log('📝 Form data:', formData);
    
    // Basic validation
    if (!formData.clientName.trim()) {
      toast.error('Please enter client name');
      return;
    }
    if (!formData.clientEmail.trim()) {
      toast.error('Please enter client email');
      return;
    }
    
    setLoading(true);
    try {
      console.log('📞 Calling quoteService.createQuote...');
      const res = await quoteService.createQuote(formData);
      console.log('✅ Quote created successfully:', res);
      
      // FIXED: Use camelCase uniqueId instead of snake_case unique_id
      const link = `${window.location.origin}/quote/${res.data.uniqueId}`;
      console.log('🔗 Generated link:', link);
      console.log('🔍 DEBUG - Checking res.data fields:', Object.keys(res.data || {}));
      console.log('🔍 DEBUG - uniqueId value:', res.data.uniqueId);
      console.log('🔍 DEBUG - unique_id value:', res.data.unique_id);
      
      if (!res.data.uniqueId) {
        console.error('❌ CRITICAL: uniqueId missing from transformed data!');
        throw new Error('Quote created but uniqueId is missing from response.');
      }
      
      setGeneratedLink(link);
      const quoteId = res.data.id || res.data.uniqueId;
      const totalValue = getQuoteTotalValue(formData);
      trackInternalQuoteCreated(quoteId, Object.keys(formData.tiers || {}).length);
      trackGA4QuoteCreated(quoteId, totalValue);
      analytics.track('Quote Link Generated', {
        quote_id: quoteId,
        value: totalValue,
        has_monthly_payments: Boolean(formData.showMonthlyPayments),
        template_used: Boolean(formData.templateUsedId)
      });
      toast.success('Quote created!');
    } catch (err) {
      console.error('❌ Quote creation failed:', err);
      toast.error(err.message || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setGeneratedLink('');
    setSelectedTemplate(null);
  };

  const handleCopyGeneratedLink = async () => {
    await navigator.clipboard.writeText(generatedLink);
    const quoteId = generatedLink.split('/quote/')[1] || generatedLink;
    trackQuoteLinkCopied(quoteId);
    analytics.track('Quote Link Copied', { quote_id: quoteId });
    toast.success('Quote link copied');
  };

  if (generatedLink) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-jetbrains">
        <div className="max-w-xl mx-auto px-4 md:px-6 py-8 md:py-16 text-center">
          <CheckCircle className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4" />
          <h1 className="font-instrument text-3xl md:text-4xl tracking-tight mb-2">Quote Created!</h1>
          <p className="mb-6 md:mb-8 text-base">Your interactive link is ready:</p>
          <div className="flex flex-col sm:flex-row rounded-lg border border-black overflow-hidden">
            <input
              readOnly
              value={generatedLink}
              className="flex-1 px-4 py-3 font-jetbrains text-sm md:text-base min-h-[48px]"
            />
            <button
              onClick={handleCopyGeneratedLink}
              className="px-4 py-3 bg-black text-white hover:bg-gray-800 transition min-h-[48px] sm:min-w-[80px]"
            >
              Copy
            </button>
          </div>
          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-black rounded-lg hover:bg-black hover:text-white transition min-h-[48px]"
            >
              Dashboard
            </button>
            <button
              onClick={resetForm}
              className="px-6 py-3 border border-black rounded-lg hover:bg-black hover:text-white transition min-h-[48px]"
            >
              New Quote
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- render ---------- */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif&family=JetBrains+Mono:wght@400;500&display=swap');
        .font-instrument { font-family: 'Instrument Serif', serif; }
        .font-jetbrains { font-family: 'JetBrains Mono', monospace; }
      `}</style>
      <div className="min-h-screen bg-white">
        <QuoteBuilderHeader onBack={() => navigate('/dashboard')} />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <form onSubmit={handleSubmit} className="space-y-8 md:space-y-12">
          {/* Template Manager */}
          <div className="border border-black rounded-2xl p-4 md:p-6">
            <h2 className="font-instrument text-2xl md:text-3xl tracking-tight mb-4">Quick-Start Templates</h2>
            {selectedTemplate && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold font-instrument text-blue-900">Template Loaded: {selectedTemplate.name}</div>
                    {selectedTemplate.description && (
                      <div className="text-sm text-blue-700 font-jetbrains">{selectedTemplate.description}</div>
                    )}
                  </div>
                  <div className="text-xs text-blue-600 font-jetbrains">
                    Used {selectedTemplate.usage_count || 0} times
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={openLoadModal}
                className="px-4 py-3 border border-black rounded-lg hover:bg-black hover:text-white transition min-h-[48px] text-center"
              >
                Load Template
              </button>
              <button
                type="button"
                onClick={openSaveModal}
                className="px-4 py-3 border border-black rounded-lg hover:bg-black hover:text-white transition min-h-[48px] text-center"
              >
                Save Template
              </button>
              {selectedTemplate && (
                <button
                  type="button"
                  onClick={clearSelectedTemplate}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-gray-600 min-h-[48px] text-center"
                >
                  Clear Template
                </button>
              )}
            </div>
          </div>

          {/* Client Info */}
          <div className="border border-black rounded-2xl p-4 md:p-6">
            <h2 className="font-instrument text-2xl md:text-3xl tracking-tight mb-4">Client Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <FormInput
                label="Client Name *"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                required
              />
              <FormInput
                label="Client Email *"
                name="clientEmail"
                type="email"
                value={formData.clientEmail}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Interactive Features */}
          <div className="border border-black rounded-2xl p-4 md:p-6">
            <h2 className="font-instrument text-2xl md:text-3xl tracking-tight mb-4">Interactive Features</h2>
            <label className="flex items-center space-x-3 min-h-[48px]">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={formData.interactiveEnabled}
                onChange={e => setFormData(prev => ({ ...prev, interactiveEnabled: e.target.checked }))}
              />
              <span className="text-base">Allow clients to add extra products</span>
            </label>
          </div>

          {/* HVAC P0: Monthly Payment Options */}
          <div className="border border-black rounded-2xl p-4 md:p-6">
            <h2 className="font-instrument text-2xl md:text-3xl tracking-tight mb-4">Payment Options</h2>
            
            {/* Show Monthly Payments Toggle */}
            <div className="mb-6">
              <label className="flex items-center space-x-3 min-h-[48px]">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={formData.showMonthlyPayments}
                  onChange={e => setFormData(prev => ({ ...prev, showMonthlyPayments: e.target.checked }))}
                />
                <span className="text-base">Show monthly payment options</span>
              </label>
              <p className="text-sm text-gray-600 mt-2 font-jetbrains">
                Display "From $X/mo" next to each package price with financing terms
              </p>
            </div>

            {/* Financing Terms (only show if monthly payments enabled) */}
            {formData.showMonthlyPayments && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 pt-4 border-t border-gray-200">
                <FormInput
                  label="APR (%)"
                  type="number"
                  name="financingApr"
                  value={formData.financingApr}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  max="100"
                />
                <FormInput
                  label="Term (Months)"
                  type="number"
                  name="financingTermMonths"
                  value={formData.financingTermMonths}
                  onChange={handleChange}
                  min="1"
                  max="360"
                />
                <FormInput
                  label="Down Payment ($)"
                  type="number"
                  name="financingDownPayment"
                  value={formData.financingDownPayment}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </div>
            )}
          </div>

          {/* HVAC P0: Rebates & Incentives */}
          <div className="border border-black rounded-2xl p-4 md:p-6">
            <h2 className="font-instrument text-2xl md:text-3xl tracking-tight mb-4">Rebates & Incentives</h2>
            <p className="text-sm text-gray-600 mb-6 font-jetbrains">
              Add estimated rebates and incentives to show customers potential savings
            </p>

            {/* Rebates List */}
            <div className="space-y-4">
              {formData.rebates.map((rebate, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                    <FormInput
                      label="Rebate Name"
                      value={rebate.label || ''}
                      onChange={e => {
                        const newRebates = [...formData.rebates];
                        newRebates[index] = { ...newRebates[index], label: e.target.value };
                        setFormData(prev => ({ ...prev, rebates: newRebates }));
                      }}
                      placeholder="e.g., Federal Tax Credit"
                    />
                    <div>
                      <label className="block text-sm md:text-base font-semibold mb-2 font-jetbrains">Type</label>
                      <select 
                        className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base min-h-[48px]"
                        value={rebate.amountType || 'fixed'}
                        onChange={e => {
                          const newRebates = [...formData.rebates];
                          newRebates[index] = { ...newRebates[index], amountType: e.target.value };
                          setFormData(prev => ({ ...prev, rebates: newRebates }));
                        }}
                      >
                        <option value="fixed">Fixed Amount ($)</option>
                        <option value="percentage">Percentage (%)</option>
                      </select>
                    </div>
                    <FormInput
                      label={rebate.amountType === 'percentage' ? 'Percentage' : 'Amount ($)'}
                      type="number"
                      value={rebate.amountValue || ''}
                      onChange={e => {
                        const newRebates = [...formData.rebates];
                        newRebates[index] = { ...newRebates[index], amountValue: e.target.value };
                        setFormData(prev => ({ ...prev, rebates: newRebates }));
                      }}
                      step="0.01"
                      min="0"
                      placeholder={rebate.amountType === 'percentage' ? '30' : '2000'}
                    />
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          const newRebates = formData.rebates.filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, rebates: newRebates }));
                        }}
                        className="w-full px-4 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition font-semibold min-h-[48px]"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <FormInput
                    label="Notes (Optional)"
                    value={rebate.notes || ''}
                    onChange={e => {
                      const newRebates = [...formData.rebates];
                      newRebates[index] = { ...newRebates[index], notes: e.target.value };
                      setFormData(prev => ({ ...prev, rebates: newRebates }));
                    }}
                    placeholder="e.g., Subject to income limits, expires Dec 31st"
                  />
                </div>
              ))}

              {/* Add Rebate Button */}
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    rebates: [...prev.rebates, { 
                      label: '', 
                      amountType: 'fixed', 
                      amountValue: '', 
                      notes: '' 
                    }]
                  }));
                }}
                className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold text-gray-700"
              >
                <Plus size={16} />
                <span>Add Rebate/Incentive</span>
              </button>
            </div>
          </div>

          {/* Optional Add-Ons */}
          <div className="border border-black rounded-2xl p-4 md:p-6">
            <h2 className="font-instrument text-2xl md:text-3xl tracking-tight mb-4">Optional Add-Ons</h2>
            <p className="text-sm text-gray-600 mb-6 font-jetbrains">
              Add optional items and services that customers can select and adjust quantities for
            </p>

            {/* Add-Ons List */}
            <div className="space-y-4">
              {formData.addons.map((addon, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3">
                    <FormInput
                      label="Add-On Name"
                      value={addon.label || ''}
                      onChange={e => {
                        const newAddons = [...formData.addons];
                        newAddons[index] = { ...newAddons[index], label: e.target.value };
                        setFormData(prev => ({ ...prev, addons: newAddons }));
                      }}
                      placeholder="e.g., Line Set Extension"
                    />
                    <FormInput
                      label="Unit Price ($)"
                      type="number"
                      value={addon.unitPrice || ''}
                      onChange={e => {
                        const newAddons = [...formData.addons];
                        newAddons[index] = { ...newAddons[index], unitPrice: e.target.value };
                        setFormData(prev => ({ ...prev, addons: newAddons }));
                      }}
                      step="0.01"
                      min="0"
                      placeholder="15.00"
                    />
                    <div>
                      <label className="block text-sm md:text-base font-semibold mb-2 font-jetbrains">Apply To</label>
                      <select 
                        className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base min-h-[48px]"
                        value={addon.tierLevel || 'all'}
                        onChange={e => {
                          const newAddons = [...formData.addons];
                          newAddons[index] = { ...newAddons[index], tierLevel: e.target.value };
                          setFormData(prev => ({ ...prev, addons: newAddons }));
                        }}
                      >
                        <option value="all">All Packages</option>
                        <option value="good">Good Package Only</option>
                        <option value="better">Better Package Only</option>
                        <option value="best">Best Package Only</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <FormInput
                        label="Min Qty"
                        type="number"
                        value={addon.quantityMin || ''}
                        onChange={e => {
                          const newAddons = [...formData.addons];
                          newAddons[index] = { ...newAddons[index], quantityMin: e.target.value };
                          setFormData(prev => ({ ...prev, addons: newAddons }));
                        }}
                        min="0"
                        placeholder="0"
                      />
                      <FormInput
                        label="Max Qty"
                        type="number"
                        value={addon.quantityMax || ''}
                        onChange={e => {
                          const newAddons = [...formData.addons];
                          newAddons[index] = { ...newAddons[index], quantityMax: e.target.value };
                          setFormData(prev => ({ ...prev, addons: newAddons }));
                        }}
                        min="0"
                        placeholder="10"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          const newAddons = formData.addons.filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, addons: newAddons }));
                        }}
                        className="w-full px-4 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition font-semibold min-h-[48px]"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Default Quantity"
                      type="number"
                      value={addon.defaultQuantity || ''}
                      onChange={e => {
                        const newAddons = [...formData.addons];
                        newAddons[index] = { ...newAddons[index], defaultQuantity: e.target.value };
                        setFormData(prev => ({ ...prev, addons: newAddons }));
                      }}
                      min="0"
                      placeholder="1"
                    />
                    <div className="flex items-center space-x-4 pt-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={addon.isPreselected || false}
                          onChange={e => {
                            const newAddons = [...formData.addons];
                            newAddons[index] = { ...newAddons[index], isPreselected: e.target.checked };
                            setFormData(prev => ({ ...prev, addons: newAddons }));
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Pre-selected in Better/Best</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Add-On Button */}
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    addons: [...prev.addons, { 
                      label: '', 
                      unitPrice: '', 
                      quantityMin: 0,
                      quantityMax: 10,
                      defaultQuantity: 1,
                      tierLevel: 'all',
                      isPreselected: false,
                      displayOrder: prev.addons.length
                    }]
                  }));
                }}
                className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold text-gray-700"
              >
                <Plus size={16} />
                <span>Add Custom Add-On</span>
              </button>
            </div>
          </div>

          {/* Tiers - Mobile Stacked Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
            {['good', 'better', 'best'].map(tier => (
              <div
                key={tier}
                className="border border-black rounded-2xl p-4 md:p-6 space-y-4"
              >
                <h3 className="font-instrument text-xl md:text-2xl tracking-tight capitalize">
                  {tier} Package *
                </h3>
                <FormInput
                  label="Title"
                  name={`${tier}.title`}
                  value={formData.tiers[tier].title}
                  onChange={handleChange}
                  required
                />
                <FormInput
                  label="Price ($)"
                  type="number"
                  name={`${tier}.price`}
                  value={formData.tiers[tier].price}
                  onChange={handleChange}
                  required
                />
                <FormTextarea
                  label="Description"
                  name={`${tier}.description`}
                  value={formData.tiers[tier].description}
                  onChange={handleChange}
                  rows={3}
                  required
                />

                <div>
                  <label className="block text-sm font-semibold mb-2 font-jetbrains">
                    Features
                  </label>
                  {formData.tiers[tier].bulletPoints.map((b, i) => (
                    <div key={i} className="flex items-center space-x-2 mb-2">
                      <input
                        value={b}
                        onChange={e => handleBullet(tier, i, e.target.value)}
                        className="flex-1 px-3 py-3 border border-black rounded min-h-[48px] text-base"
                        placeholder="Enter feature description"
                      />
                      <button
                        type="button"
                        onClick={() => removeBullet(tier, i)}
                        className="mobile-action-btn bg-red-100 hover:bg-red-200 text-red-700"
                        disabled={formData.tiers[tier].bulletPoints.length <= 1}
                        aria-label="Remove feature"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addBullet(tier)}
                    className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition min-h-[44px]"
                  >
                    <Plus size={16} />
                    <span>Add feature</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Submit - Full Width on Mobile */}
          <div className="flex justify-end">
            <PrimaryButton 
              type="submit" 
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? 'Creating…' : 'Save & Generate Link'}
            </PrimaryButton>
          </div>
          </form>
        </main>
      </div>

      {/* ADDED: Save Template Modal */}
      <Modal show={showSaveModal} onClose={closeSaveModal} title="Save Template">
        <div className="space-y-4">
          <FormInput
            label="Template Name *"
            value={saveTemplateForm.templateName}
            onChange={(e) => setSaveTemplateForm(prev => ({ ...prev, templateName: e.target.value }))}
            placeholder="Enter template name"
            required
          />
          <FormTextarea
            label="Description (Optional)"
            value={saveTemplateForm.templateDescription}
            onChange={(e) => setSaveTemplateForm(prev => ({ ...prev, templateDescription: e.target.value }))}
            placeholder="Describe when to use this template"
            rows={3}
          />
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={closeSaveModal}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveAsTemplate}
              disabled={!saveTemplateForm.templateName.trim()}
              className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 disabled:opacity-50"
            >
              Save Template
            </button>
          </div>
        </div>
      </Modal>

      {/* ADDED: Load Template Modal */}
      <Modal show={showLoadModal} onClose={closeLoadModal} title="Load Template">
        <div className="space-y-4">
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 font-jetbrains">No templates saved yet.</p>
              <p className="text-sm text-gray-500 font-jetbrains mt-2">Create your first template by filling out the form and clicking 'Save Template'.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {templates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:border-black transition">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold font-instrument">{template.name}</h4>
                      {template.description && (
                        <p className="text-sm text-gray-600 font-jetbrains mt-1">{template.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          loadTemplate(template.id);
                          closeLoadModal();
                        }}
                        className="px-3 py-1 bg-black text-white rounded text-sm hover:bg-gray-800 transition"
                      >
                        Load
                      </button>
                      {!template.is_builtin && (
                        <button
                          type="button"
                          onClick={() => deleteTemplate(template.id)}
                          className="px-3 py-1 border border-red-300 text-red-600 rounded text-sm hover:bg-red-50 transition"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs font-jetbrains text-gray-500">
                    <div>Good: ${template.good_price}</div>
                    <div>Better: ${template.better_price}</div>
                    <div>Best: ${template.best_price}</div>
                  </div>
                  {(template.is_builtin || template.is_default) && (
                    <div className="mt-2 flex items-center gap-2">
                      {template.is_builtin && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Built-in
                        </span>
                      )}
                      {template.is_default && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Default
                      </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={closeLoadModal}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default QuoteBuilder;
